// components/common/dropdowns/InlineOwnerField.tsx
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import UserAvatar from 'components/common/UserAvatar';
import OwnerSearchDropdown from './OwnerSearchDropdown';
import { ContactOwner } from 'types/crm/contacts';

/** Minimal owner shape, so ContactOwner / CompanyOwner / deal owners all fit. */
interface OwnerLike {
  uuid: string;
  name: string;
  profileImageURL?: string | null;
}

interface InlineOwnerFieldProps<O extends OwnerLike> {
  label?: string;
  owners: O[];
  /** `null` means explicitly unassigned. */
  value: string | null;
  /** PATCH the owner. Return the promise (mutateAsync) so we can roll back. */
  onSave: (ownerUuid: string | null) => Promise<unknown>;
  /** False → static avatar + name, no dropdown. Pass your role check here. */
  editable?: boolean;
  disabled?: boolean;
  allowUnassigned?: boolean;
  unassignedLabel?: string;
  placeholder?: string;
  size?: 'sm' | 'md' | 'lg';
  errorMessage?: string;
  className?: string;
}

/** OwnerSearchDropdown that PATCHes on select, with the same label as LabeledSelectDropdown. */
function InlineOwnerField<O extends OwnerLike>({
  label = 'Owner',
  owners,
  value,
  onSave,
  editable = true,
  disabled = false,
  allowUnassigned = true,
  unassignedLabel = 'Unassigned',
  placeholder = 'Unassigned',
  size = 'md',
  errorMessage = 'Could not update the owner. Please try again.',
  className = '',
}: InlineOwnerFieldProps<O>) {
  // `undefined` = nothing pending, `null` = pending unassign.
  const [pending, setPending] = useState<string | null | undefined>(undefined);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (pending !== undefined && value === pending) setPending(undefined);
  }, [value, pending]);

  const currentUuid = pending !== undefined ? pending : value;
  const currentOwner = owners.find((o) => o.uuid === currentUuid) ?? null;

  const handleChange = async (owner: OwnerLike | null) => {
    const next = owner ? owner.uuid : null;
    if (next === currentUuid) return;

    setPending(next);
    setIsSaving(true);
    try {
      await onSave(next);
    } catch (err: any) {
      setPending(undefined);
      toast.error(err?.message ?? errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="w-full">
      {label && (
        <div className="text-[11px] font-medium tracking-wide uppercase text-neutral-medium mb-1.5">
          {label}
          {isSaving && <span className="ml-1.5 normal-case tracking-normal">Saving…</span>}
        </div>
      )}

      {editable ? (
        <OwnerSearchDropdown
          // OwnerSearchDropdown is typed to ContactOwner; CompanyOwner and the
          // deal owner are the same shape, so widen here instead of duplicating it.
          owners={owners as unknown as ContactOwner[]}
          currentOwnerUuid={currentUuid}
          onOwnerChange={handleChange}
          disabled={disabled || isSaving}
          allowUnassigned={allowUnassigned}
          unassignedLabel={unassignedLabel}
          placeholder={placeholder}
          size={size}
          className={`bg-primary-light border border-primary-subtle rounded-lg ${className}`}
        />
      ) : (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-primary-light border border-primary-subtle">
          {currentOwner ? (
            <>
              <UserAvatar
                name={currentOwner.name}
                profileImageURL={currentOwner.profileImageURL ?? undefined}
                className="w-7 h-7 text-xs"
              />
              <span className="text-sm text-neutral truncate">{currentOwner.name}</span>
            </>
          ) : (
            <span className="text-sm text-neutral-medium">{unassignedLabel}</span>
          )}
        </div>
      )}
    </div>
  );
}

export default InlineOwnerField;